import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import { Building2, Loader2, LockKeyhole } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { guestPortalAuthApi } from "@/api";
import guestBg from "@/assets/images/guest-bg.jpg";
import { toast } from "sonner";
import { saveGuestSession } from "@/utils/guestSessionStorage.js";

const getApiData = (response) =>
  response?.data?.data || response?.data?.Data || response?.data || {};

const getApiMessage = (error, fallback = "Something went wrong.") =>
  error?.response?.data?.message ||
  error?.response?.data?.Message ||
  error?.response?.data?.title ||
  fallback;

const passcodeModeText = {
  AutoGenerated4DigitPin: "Enter your 4-digit check-in PIN.",
  AutoGenerated6DigitPin: "Enter your 6-digit check-in PIN.",
  GuestLastName: "Enter the registered guest last name.",
  PhoneLast4Digits: "Enter the last 4 digits of your registered phone number.",
  CustomCode: "Enter your check-in code.",
  1: "Enter your 4 digit check-in PIN.",
  2: "Enter your 6 digit check-in PIN.",
  3: "Enter the registered guest last name.",
  4: "Enter the last 4 digits of your registered phone number.",
  5: "Enter your check-in code.",
};

const RoomQrEntryPage = () => {
  const { roomPublicCode } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [passcode, setPasscode] = useState("");
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const hasLoadedRef = useRef(false);
  const requestIdRef = useRef(0);

  const loadStatus = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    try {
      if (!hasLoadedRef.current) {
        setInitialLoading(true);
      } else {
        setRefreshing(true);
      }

      sessionStorage.setItem("guestRoomPublicCode", roomPublicCode);
      const response = await guestPortalAuthApi.roomStatus(roomPublicCode);
      if (requestId !== requestIdRef.current) return;

      const data = getApiData(response);
      setStatus(data);
      hasLoadedRef.current = true;

      if (data?.isAuthenticated || data?.IsAuthenticated) {
        try {
          const me = await guestPortalAuthApi.me();
          if (requestId !== requestIdRef.current) return;
          saveGuestSession(getApiData(me));
          navigate("/guest/home", { replace: true });
        } catch {
          if (requestId !== requestIdRef.current) return;
          setStatus({
            ...data,
            isAuthenticated: false,
            IsAuthenticated: false,
            requiresPasscode: true,
            RequiresPasscode: true,
            message: "Please enter your check-in passcode.",
          });
        }
      }
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      if (!hasLoadedRef.current) {
        toast.error(getApiMessage(error, "Could not load room QR."));
      }
      hasLoadedRef.current = true;
      setStatus({
        roomFound: false,
        qrActive: false,
        message: "This QR code is no longer active. Please contact reception.",
      });
    } finally {
      if (requestId === requestIdRef.current) {
        setInitialLoading(false);
        setRefreshing(false);
      }
    }
  }, [navigate, roomPublicCode]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleVerify = async (event) => {
    event.preventDefault();
    if (!passcode.trim()) {
      toast.error("Please enter your passcode.");
      return;
    }

    try {
      setVerifying(true);
      const response = await guestPortalAuthApi.verify(roomPublicCode, {
        passcode: passcode.trim(),
      });
      const sessionData = getApiData(response);
      saveGuestSession(sessionData);
      toast.success("Room verified successfully");
      navigate("/guest/home", { replace: true });
    } catch (error) {
      toast.error(getApiMessage(error, "Unable to verify this room."));
    } finally {
      setVerifying(false);
    }
  };

  const activeSession =
    status?.sessionActive ??
    status?.SessionActive ??
    status?.activeSessionExists ??
    status?.ActiveSessionExists ??
    false;
  const qrActive = status?.qrActive ?? status?.QrActive ?? false;
  const roomFound = status?.roomFound ?? status?.RoomFound ?? false;
  const requiresPasscode =
    status?.requiresPasscode ??
    status?.RequiresPasscode ??
    (activeSession && !(status?.authenticated ?? status?.Authenticated ?? status?.isAuthenticated ?? status?.IsAuthenticated));
  const message = status?.message || status?.Message;
  const roomName = status?.roomName || status?.RoomName;
  const passcodeMode = status?.passcodeMode || status?.PasscodeMode;
  const passcodePrompt =
    passcodeModeText[passcodeMode] ||
    status?.passcodeModeLabel ||
    status?.PasscodeModeLabel ||
    "Please enter your check-in passcode to continue.";

  return (
    <>
      <Helmet>
        <title>Room Access - The Hotelier</title>
      </Helmet>

      <div className="relative min-h-[100dvh] overflow-hidden bg-[#16201b]">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-75"
          style={{ backgroundImage: `url(${guestBg})` }}
        />
        <div className="absolute inset-0 bg-black/35" />

        <main className="relative z-10 flex min-h-[100dvh] items-center justify-center px-4 py-8">
          <div className="w-full max-w-md rounded-3xl border border-white/70 bg-white/92 p-6 shadow-2xl backdrop-blur-xl">
            <div className="mb-6 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#923536] text-white">
                <Building2 className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Room access</h1>
                <p className="text-sm text-muted-foreground">
                  {roomName ? `Room ${roomName}` : "Guest portal"}
                </p>
              </div>
            </div>

            {initialLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-10 w-10 animate-spin text-[#923536]" />
                <span className="sr-only">Checking room access...</span>
              </div>
            ) : !roomFound || !qrActive || !activeSession ? (
              <div className="rounded-2xl border border-dashed p-5 text-center">
                <p className="font-medium text-slate-800">{message}</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Please contact reception for assistance.
                </p>
              </div>
            ) : requiresPasscode ? (
              <form onSubmit={handleVerify} className="space-y-4">
                <div className="rounded-2xl bg-slate-50 p-4">
                  <div className="mb-3 flex items-center gap-2 text-sm font-medium">
                    <LockKeyhole className="h-4 w-4 text-[#923536]" />
                    Please enter your check-in passcode to continue.
                  </div>
                  <p className="mb-3 text-sm text-muted-foreground">
                    {refreshing ? "Verifying your stay..." : passcodePrompt}
                  </p>
                  <Input
                    value={passcode}
                    onChange={(event) => setPasscode(event.target.value)}
                    placeholder="Enter passcode"
                    autoFocus
                  />
                </div>

                <Button
                  type="submit"
                  disabled={verifying}
                  className="w-full bg-[#923536] text-white hover:bg-[#7f2e2f]"
                >
                  {verifying && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Continue
                </Button>

                <p className="text-center text-xs text-muted-foreground">
                  Need help? Please contact reception.
                </p>
              </form>
            ) : (
              <div className="rounded-2xl border border-dashed p-5 text-center">
                <p className="font-medium text-slate-800">
                  Guest access is ready.
                </p>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default RoomQrEntryPage;
